'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { RoomInfoPanel } from '@/components/public/room-info-panel';
import { ImageLightbox } from '@/components/public/image-lightbox';
import type { LightboxImage } from '@/components/public/image-lightbox';
import type { Room } from '@/types/database';

export default function SwanSuiteContent({ room }: { room: Room }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useTranslations('roomContent.the-swan-suite');
  const bold = { b: (chunks: ReactNode) => <strong>{chunks}</strong> };

  // All gallery images in display order (for lightbox navigation)
  const GALLERY_IMAGES: LightboxImage[] = [
    { src: '/design-images/rooms/the-swan-suite/detail.jpg', alt: t('alt.detail'), width: 1920, height: 1281 },
    { src: '/design-images/rooms/the-swan-suite/story.jpg', alt: t('alt.story'), width: 1709, height: 2560 },
    { src: '/design-images/rooms/the-swan-suite/gallery-1.jpg', alt: t('alt.gallery1'), width: 1920, height: 1268 },
    { src: '/design-images/rooms/the-swan-suite/gallery-2.jpg', alt: t('alt.gallery2'), width: 1920, height: 1324 },
    { src: '/design-images/rooms/the-swan-suite/gallery-3.jpg', alt: t('alt.gallery3'), width: 1920, height: 1283 },
    { src: '/design-images/rooms/the-swan-suite/gallery-4.jpg', alt: t('alt.gallery4'), width: 1398, height: 2560 },
    { src: '/design-images/rooms/the-swan-suite/gallery-5.jpg', alt: t('alt.gallery5'), width: 1920, height: 2046 },
  ];

  return (
    <div id="room-content" className="w-full flex flex-col">
      {/* White spacer between hero and first section */}
      <div className="bg-white h-2 w-full" />

      {/* Section: Room Benefits — side-by-side, text left + image right */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="w-full flex items-center py-6 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                  <h1 className="font-heading font-bold mb-4 text-4xl md:text-5xl">
                    <strong>{t('benefits.titleLine1')}</strong>
                    <br />
                    <strong>{t('benefits.titleLine2')}</strong>
                  </h1>
                  <p className="mb-4">{t('benefits.p1')}</p>
                  <p className="mb-4">{t('benefits.p2')}</p>
                  <p className="mb-4">{t('benefits.p3')}</p>
                </div>
                <RoomInfoPanel room={room} />
              </div>
            </div>
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src="/design-images/rooms/the-swan-suite/detail.jpg"
                alt={t('alt.detail')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Story — side-by-side, image left + text right */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(1)}
            >
              <Image
                src="/design-images/rooms/the-swan-suite/story.jpg"
                alt={t('alt.story')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="w-full flex items-center py-8 md:py-12 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                  <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('story.title')}</h1>
                  <div className="w-16 h-1 bg-[#801515] mb-8" />
                  <p className="mb-6 text-lg md:text-xl leading-relaxed font-light italic text-gray-700">{t.rich('story.p1', bold)}</p>
                  <p className="mb-6 text-base md:text-lg leading-relaxed text-gray-700">{t.rich('story.p2', bold)}</p>
                  <p className="text-base md:text-lg leading-relaxed text-gray-700">{t.rich('story.p3', bold)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Gallery: Full-width landscape images */}
      <section className="w-full mt-2 cursor-pointer" onClick={() => setLightboxIndex(2)}>
        <Image
          src="/design-images/rooms/the-swan-suite/gallery-1.jpg"
          alt={t('alt.gallery1')}
          width={1920}
          height={1268}
          className="w-full h-auto"
          sizes="100vw"
        />
      </section>

      <section className="w-full mt-2 cursor-pointer" onClick={() => setLightboxIndex(3)}>
        <Image
          src="/design-images/rooms/the-swan-suite/gallery-2.jpg"
          alt={t('alt.gallery2')}
          width={1920}
          height={1324}
          className="w-full h-auto"
          sizes="100vw"
        />
      </section>

      <section className="w-full mt-2 cursor-pointer" onClick={() => setLightboxIndex(4)}>
        <Image
          src="/design-images/rooms/the-swan-suite/gallery-3.jpg"
          alt={t('alt.gallery3')}
          width={1920}
          height={1283}
          className="w-full h-auto"
          sizes="100vw"
        />
      </section>

      {/* Paired portrait images — grid-cols-2 on all breakpoints to prevent full-width portraits on mobile */}
      <div className="grid grid-cols-2 gap-0.5 mt-2">
        <div className="cursor-pointer" onClick={() => setLightboxIndex(5)}>
          <Image
            src="/design-images/rooms/the-swan-suite/gallery-4.jpg"
            alt={t('alt.gallery4')}
            width={1398}
            height={2560}
            className="w-full h-auto"
            sizes="50vw"
          />
        </div>
        <div className="cursor-pointer" onClick={() => setLightboxIndex(6)}>
          <Image
            src="/design-images/rooms/the-swan-suite/gallery-5.jpg"
            alt={t('alt.gallery5')}
            width={1920}
            height={2046}
            className="w-full h-auto"
            sizes="50vw"
          />
        </div>
      </div>

      {/* Video section */}
      <section className="w-full mt-2">
        <div className="max-w-3xl mx-auto px-6 md:px-8 lg:px-12">
          <video
            src="https://cxtacwxwreiineqjrbci.supabase.co/storage/v1/object/public/design-videos/uploads/1782913348240-5.mp4"
            autoPlay
            controls
            playsInline
            muted
            loop
            className="w-full h-auto rounded-lg"
          />
        </div>
      </section>

      {/* Fullscreen gallery lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={GALLERY_IMAGES}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
